// frontend/src/components/athleteEditor/RailCards.jsx
// 320px rail cards for the 1a screen: Photos (+ trading cards/videos summary),
// Contact (merged with Guardians), Account. Ported from the page's old
// PhotosSection/ContactSection/GuardiansSection into the `.ae-card` shell
// with the compact rail styles from the design spec.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { groupVideosByContentType } from '../../constants/videoContentTypes';
import { fmtDate, fmtLastSeen } from '../../lib/staffProfileFormat';
import { useCampPhotos, proxiedCampPhoto } from '../CampPhotos';

// Small local humanize (the page's shared one was deleted when its sections
// moved here) — turns snake/kebab keys into Title Case for video categories
// and template ids.
const humanize = s => (s ? String(s).replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '');

// player_parent/base_user/athlete all read as "Athlete" — same map used
// across the admin pages (AdminUsersPage.js, AdminReportsPage.js, etc.).
// Task 3 dropped the page's local ROLE_LABELS map; reintroduced here since
// AccountRailCard is the only remaining consumer on this page.
const ROLE_LABELS = {
  athlete: 'Athlete',
  base_user: 'Base User',
  player_parent: 'Athlete/Parent',
  coach: 'Coach',
  basic_coach: 'Coach (Pending)',
  partner: 'Partner',
  guardian: 'Guardian/Parent',
  admin: 'Admin',
  internal_staff: 'Internal Staff',
};

// School logo rendered same-origin via the backend image proxy, with a
// graceful initials fallback when the image can't load.
function SchoolLogo({ url, schoolName, apiUrl }) {
  const [failed, setFailed] = useState(false);
  const proxied = url ? `${apiUrl}/image-proxy?url=${encodeURIComponent(url)}` : null;
  const initials = (schoolName || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '🏫';
  return !failed && proxied ? (
    <img src={proxied} alt="School logo" onError={() => setFailed(true)} className="ae-photo-2up__logo-img" />
  ) : (
    <span className="ae-photo-2up__logo-fallback">{initials}</span>
  );
}

export function PhotosRailCard({ userId, profile, apiUrl, cards, videos, personalVideoUrl, fixSchoolLogoHref }) {
  const safe = profile || {};
  // Staff-captured camp photos live apart from the 3 athlete-chosen slots.
  const campPhotos = useCampPhotos(userId, apiUrl);
  const photos = [
    { src: safe.player_photo, label: 'PHOTO 1', slot: 1 },
    { src: safe.player_photo_2, label: 'PHOTO 2', slot: 2 },
    { src: safe.player_photo_3, label: 'PHOTO 3', slot: 3 },
  ].filter(p => p.src);
  const primaryIdx = safe.primary_photo_index || 1;
  const hasLogo = !!safe.school_logo;
  const tileCount = photos.length + campPhotos.length + (hasLogo ? 1 : 0);
  const cardList = cards || [];
  const videoList = videos || [];
  const hasVideos = videoList.length > 0 || !!personalVideoUrl;

  return (
    <div className="ae-card ae-rail-card">
      <div className="ae-rail-card__head">
        <span className="ae-rail-card__title">Photos</span>
        {tileCount > 0 && <span className="ae-rail-card__count">{tileCount}</span>}
      </div>
      {tileCount === 0 ? (
        <div className="ae-rail-card__empty">No photos uploaded.</div>
      ) : (
        <div className="ae-photo-2up">
          {photos.map(p => (
            <a key={p.slot} href={p.src} target="_blank" rel="noopener noreferrer" className="ae-photo-2up__tile">
              <div className={`ae-photo-2up__frame${p.slot === primaryIdx ? ' ae-photo-2up__frame--primary' : ''}`}>
                <img src={p.src} alt={p.label} />
                {p.slot === primaryIdx && <span className="ae-chip--primary">PRIMARY</span>}
              </div>
              <div className="ae-photo-2up__caption">{p.label}</div>
            </a>
          ))}
          {campPhotos.map(p => (
            <a key={`camp-${p.key}`} href={proxiedCampPhoto(apiUrl, p.url)} target="_blank" rel="noopener noreferrer" className="ae-photo-2up__tile">
              <div className="ae-photo-2up__frame">
                <img src={proxiedCampPhoto(apiUrl, p.url)} alt={`Camp ${p.label}`} />
              </div>
              <div className="ae-photo-2up__caption">CAMP {p.label.toUpperCase()}</div>
            </a>
          ))}
          {hasLogo && (
            <div className="ae-photo-2up__tile">
              <a href={safe.school_logo} target="_blank" rel="noopener noreferrer">
                <div className="ae-photo-2up__frame ae-photo-2up__frame--logo">
                  <SchoolLogo url={safe.school_logo} schoolName={safe.school ?? safe.high_school} apiUrl={apiUrl} />
                </div>
              </a>
              <div className="ae-photo-2up__caption">
                SCHOOL LOGO
                {/* The crest is shared by every athlete at this school and is
                    often machine-fetched, so a wrong one (a hospital, a bank)
                    is spotted HERE -- on a profile someone is already looking
                    at -- and nowhere else. Admin-only: the fix is school-wide. */}
                {fixSchoolLogoHref && safe.high_school && (
                  <>
                    {' '}
                    <Link
                      className="ae-photo-2up__fix"
                      to={fixSchoolLogoHref}
                      title="Wrong logo? Replace it for this school"
                    >
                      FIX
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="ae-rail-card__section ae-rail-card__section--top">
        <span className="ae-rail-card__label">Trading cards</span>
        {cardList.length === 0 ? (
          <span className="ae-rail-card__value">None built yet</span>
        ) : (
          <div className="ae-rail-strip">
            {cardList.map(card => {
              const label = card.name || humanize(card.recipe?.template_id) || 'Card';
              return card.composite_url ? (
                <a
                  key={card.id} href={card.composite_url} target="_blank" rel="noopener noreferrer"
                  className="ae-rail-strip__card" title={label}
                >
                  <img src={`${apiUrl}/image-proxy?url=${encodeURIComponent(card.composite_url)}`} alt={label} />
                </a>
              ) : (
                <span key={card.id} className="ae-rail-strip__cardtext" title={label}>{label}</span>
              );
            })}
          </div>
        )}
      </div>
      <div className="ae-rail-card__section">
        <span className="ae-rail-card__label">Videos</span>
        {!hasVideos ? (
          <span className="ae-rail-card__value">None uploaded</span>
        ) : (
          <div className="ae-rail-links">
            {personalVideoUrl && (
              <a href={personalVideoUrl} target="_blank" rel="noopener noreferrer" className="ae-rail-links__item">
                Personal Video ↗
              </a>
            )}
            {groupVideosByContentType(videoList).map(group => (
              <div key={group.contentType} className="ae-rail-links__group">
                <span className="ae-rail-links__grouplabel">{group.label}</span>
                {group.videos.map(v => {
                  const label = v.label || humanize(v.category) || 'Video';
                  const meta = [v.context_label, v.clip_tag].filter(Boolean).join(' · ');
                  const text = meta ? `${label} — ${meta}` : label;
                  return v.video_url ? (
                    <a key={v.id} href={v.video_url} target="_blank" rel="noopener noreferrer" className="ae-rail-links__item" title={text}>
                      {text} ↗
                    </a>
                  ) : (
                    <span key={v.id} className="ae-rail-links__item ae-rail-links__item--plain" title={text}>{text}</span>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GuardianBlock({ label, name, relation, phone, email, approved }) {
  // `relation` alone (no name/phone/email) still counts as content — the
  // club-coach block below can have only a team name set (relation slot),
  // and that must not be swallowed as "empty".
  if (!name && !phone && !email && !relation) return null;
  return (
    <div className="ae-rail-guardian">
      <div className="ae-rail-guardian__head">
        <span className="ae-rail-guardian__cap">{label}</span>
        {approved === true && <span className="ae-chip--approved">APPROVED</span>}
        {approved === false && <span className="ae-chip--unapproved">UNAPPROVED</span>}
      </div>
      <div className="ae-rail-guardian__name">
        {name || label}{relation && <span className="ae-rail-guardian__relation"> ({relation})</span>}
      </div>
      {(phone || email) && (
        <div className="ae-rail-guardian__contact">
          {phone && <a href={`tel:${phone}`}>{phone}</a>}
          {email && <a href={`mailto:${email}`}>{email}</a>}
        </div>
      )}
    </div>
  );
}

export function ContactRailCard({ profile, user, sportSlug }) {
  const safe = profile || {};
  const u = user || {};
  const addressParts = [
    safe.home_address,
    safe.address_line_2,
    [safe.city, safe.state].filter(Boolean).join(', '),
    safe.zip_code,
  ].filter(Boolean);
  const phone = safe.cell_phone || u.phone;
  const dob = u.date_of_birth
    ? new Date(u.date_of_birth).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;
  const g1Has = safe.guardian1_name || safe.guardian1_phone || safe.guardian1_email;
  const g2Has = safe.guardian2_name || safe.guardian2_phone || safe.guardian2_email;
  // Coach contact is per-sport now (a two-sport athlete has two HS coaches
  // and per-sport club teams) — show the entry for the sport being viewed
  // (the page's `sportSlug`, derived from profile.sport), same as every
  // other sport-scoped card on this page.
  const scList = Array.isArray(safe.sport_coaches) ? safe.sport_coaches : [];
  const sc = scList.find(r => r.sport_slug === sportSlug) || null;
  const hsHas = !!(sc && (sc.hs_coach_name || sc.hs_coach_phone || sc.hs_coach_email));
  const clubHas = !!(sc && (sc.club_coach_name || sc.club_coach_phone || sc.club_coach_email || sc.club_team_name));
  const hasAny = addressParts.length || phone || u.email || dob || g1Has || g2Has || hsHas || clubHas;

  if (!hasAny) {
    return (
      <div className="ae-card ae-rail-card">
        <div className="ae-rail-card__head"><span className="ae-rail-card__title">Contact</span></div>
        <div className="ae-rail-card__empty">No contact info on file.</div>
      </div>
    );
  }

  return (
    <div className="ae-card ae-rail-card">
      <div className="ae-rail-card__head"><span className="ae-rail-card__title">Contact</span></div>
      <div className="ae-rail-kv">
        {phone && (
          <div className="ae-rail-kv__row">
            <span className="ae-rail-kv__label">Phone</span>
            <span className="ae-rail-kv__value">
              <a className="ae-rail-kv__value--link" href={`tel:${phone}`}>{phone}</a>
              {safe.cell_phone && u.phone && safe.cell_phone !== u.phone && (
                <span className="ae-rail-kv__note"> (account: {u.phone})</span>
              )}
            </span>
          </div>
        )}
        {u.email && (
          <div className="ae-rail-kv__row">
            <span className="ae-rail-kv__label">Email</span>
            <span className="ae-rail-kv__value ae-rail-kv__value--truncate" title={u.email}>{u.email}</span>
          </div>
        )}
        {dob && (
          <div className="ae-rail-kv__row">
            <span className="ae-rail-kv__label">DOB</span>
            <span className="ae-rail-kv__value">{dob}</span>
          </div>
        )}
        {addressParts.length > 0 && (
          <div className="ae-rail-kv__row ae-rail-kv__row--multiline">
            <span className="ae-rail-kv__label">Address</span>
            <span className="ae-rail-kv__value">
              {addressParts.map((p, i) => <div key={i}>{p}</div>)}
            </span>
          </div>
        )}
      </div>
      {g1Has && (
        <GuardianBlock
          label="Guardian" name={safe.guardian1_name} relation={safe.guardian1_relation}
          phone={safe.guardian1_phone} email={safe.guardian1_email} approved={safe.guardian1_approved}
        />
      )}
      {g2Has && (
        <GuardianBlock
          label="Guardian 2" name={safe.guardian2_name} relation={safe.guardian2_relation}
          phone={safe.guardian2_phone} email={safe.guardian2_email} approved={undefined}
        />
      )}
      {hsHas && (
        <GuardianBlock
          label="High School Coach" name={sc.hs_coach_name}
          phone={sc.hs_coach_phone} email={sc.hs_coach_email}
        />
      )}
      {clubHas && (
        <GuardianBlock
          label="Club Coach" name={sc.club_coach_name} relation={sc.club_team_name}
          phone={sc.club_coach_phone} email={sc.club_coach_email}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------
   Staff/partner read view (`.adm` look). The three cards below carry the same
   data and the same conditions as PhotosRailCard / ContactRailCard /
   AccountRailCard above, which the coach view keeps rendering unchanged.
   ------------------------------------------------------------------------ */

function StaffSchoolLogo({ url, schoolName, apiUrl }) {
  const [failed, setFailed] = useState(false);
  const proxied = url ? `${apiUrl}/image-proxy?url=${encodeURIComponent(url)}` : null;
  const initials = (schoolName || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '—';
  return !failed && proxied
    ? <img src={proxied} alt="School logo" onError={() => setFailed(true)} />
    : <span className="adm-photo__initials">{initials}</span>;
}

// AI-given cards carry template `ai`; everything else is athlete-built.
export function cardLabel(card) {
  if (card?.recipe?.template_id === 'ai') return 'AI card';
  return card?.name || humanize(card?.recipe?.template_id) || 'Card';
}

export function StaffPhotosCard({ userId, profile, apiUrl, cards, videos, personalVideoUrl, fixSchoolLogoHref }) {
  const safe = profile || {};
  // Staff-captured camp photos live apart from the 3 athlete-chosen slots.
  const campPhotos = useCampPhotos(userId, apiUrl);
  const photos = [
    { src: safe.player_photo, label: 'Photo 1', slot: 1 },
    { src: safe.player_photo_2, label: 'Photo 2', slot: 2 },
    { src: safe.player_photo_3, label: 'Photo 3', slot: 3 },
  ].filter(p => p.src);
  const primaryIdx = safe.primary_photo_index || 1;
  const hasLogo = !!safe.school_logo;
  const tileCount = photos.length + campPhotos.length + (hasLogo ? 1 : 0);
  const cardList = cards || [];
  const videoList = videos || [];
  const hasVideos = videoList.length > 0 || !!personalVideoUrl;

  return (
    <section className="adm__card adm-rcard">
      <div className="adm-card__head">
        <h2 className="adm-card__title">Photos</h2>
        {tileCount > 0 && <span className="adm-card__meta">{tileCount}</span>}
      </div>
      {tileCount === 0 ? (
        <div className="adm-photos__empty">No photos uploaded.</div>
      ) : (
        <div className="adm-photos">
          {photos.map(p => (
            <a key={p.slot} href={p.src} target="_blank" rel="noopener noreferrer" className="adm-photo">
              <div className={`adm-photo__frame${p.slot === primaryIdx ? ' adm-photo__frame--primary' : ''}`}>
                <img src={p.src} alt={p.label} />
                {p.slot === primaryIdx && <span className="adm-photo__primary">Primary</span>}
              </div>
              <div className="adm-photo__caption">{p.label}</div>
            </a>
          ))}
          {campPhotos.map(p => (
            <a key={`camp-${p.key}`} href={proxiedCampPhoto(apiUrl, p.url)} target="_blank" rel="noopener noreferrer" className="adm-photo">
              <div className="adm-photo__frame">
                <img src={proxiedCampPhoto(apiUrl, p.url)} alt={`Camp ${p.label}`} />
              </div>
              <div className="adm-photo__caption">Camp {p.label.toLowerCase()}</div>
            </a>
          ))}
          {hasLogo && (
            <div className="adm-photo">
              <a href={safe.school_logo} target="_blank" rel="noopener noreferrer" className="adm-photo" aria-label="Open school logo">
                <div className="adm-photo__frame adm-photo__frame--logo">
                  <StaffSchoolLogo url={safe.school_logo} schoolName={safe.school ?? safe.high_school} apiUrl={apiUrl} />
                </div>
              </a>
              <div className="adm-photo__caption">
                School logo
                {/* Admin-only, school-wide fix -- see PhotosRailCard. */}
                {fixSchoolLogoHref && safe.high_school && (
                  <>
                    {' · '}
                    <Link to={fixSchoolLogoHref} title="Wrong logo? Replace it for this school">Fix</Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="adm-rsection">
        {/* Trading cards (AI-given and athlete-built) as tiles the size of the photos. */}
        <div className="adm-rsection__label" style={{ marginBottom: 8 }}>Trading cards</div>
        {cardList.length === 0 ? (
          <div className="adm-photos__empty">None built yet</div>
        ) : (
          <div className="adm-photos" data-testid="staff_trading_cards">
            {cardList.map(card => {
              const label = cardLabel(card);
              const caption = card.is_primary ? `${label} · primary` : label;
              return card.composite_url ? (
                <a key={card.id} href={card.composite_url} target="_blank" rel="noopener noreferrer" className="adm-photo" title={label}>
                  <div className="adm-photo__frame adm-photo__frame--card">
                    <img src={`${apiUrl}/image-proxy?url=${encodeURIComponent(card.composite_url)}`} alt={label} />
                  </div>
                  <div className="adm-photo__caption">{caption}</div>
                </a>
              ) : (
                <div key={card.id} className="adm-photo" title={label}>
                  <div className="adm-photo__frame adm-photo__frame--card"><span className="adm-photos__empty">No image</span></div>
                  <div className="adm-photo__caption">{caption}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="adm-rsection">
        <div className="adm-rline">
          <span className="adm-rline__label">Videos</span>
          {!hasVideos ? (
            <span className="adm-rline__value">None uploaded</span>
          ) : (
            <div className="adm-rlinks">
              {personalVideoUrl && (
                <a href={personalVideoUrl} target="_blank" rel="noopener noreferrer">Personal video ↗</a>
              )}
              {groupVideosByContentType(videoList).map(group => (
                <React.Fragment key={group.contentType}>
                  <span className="adm-rlinks__group">{group.label}</span>
                  {group.videos.map(v => {
                    const label = v.label || humanize(v.category) || 'Video';
                    const meta = [v.context_label, v.clip_tag].filter(Boolean).join(' · ');
                    const text = meta ? `${label} — ${meta}` : label;
                    return v.video_url ? (
                      <a key={v.id} href={v.video_url} target="_blank" rel="noopener noreferrer" title={text}>{text} ↗</a>
                    ) : (
                      <span key={v.id} className="adm-rlinks__item" title={text}>{text}</span>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StaffContactBlock({ label, name, relation, phone, email, approved, dashed, children }) {
  // Same emptiness rule as GuardianBlock: a relation alone (club team name) counts.
  if (!name && !phone && !email && !relation) return null;
  return (
    <div className={`adm-rsection${dashed ? ' adm-rsection--dashed' : ''}`}>
      <div className="adm-guardian__head">
        <span className="adm-rsection__label">{label}</span>
        {approved === true && <span className="adm-chip adm-chip--green">Approved</span>}
        {approved === false && <span className="adm-chip adm-chip--amber">Unapproved</span>}
      </div>
      <div className="adm-guardian__name">
        {name || label}{relation && <span className="adm-guardian__rel"> ({relation})</span>}
      </div>
      {(phone || email) && (
        <div className="adm-guardian__contact">
          {phone && <a className="adm-plink" href={`tel:${phone}`}>{phone}</a>}
          {email && <a className="adm-plink" href={`mailto:${email}`}>{email}</a>}
        </div>
      )}
      {children}
    </div>
  );
}

// Staff-only: does the guardian on the profile have a ProZpects account linked
// to this athlete? Typing a guardian onto the profile only stores text, so this
// is where staff turn it into an account (POST .../guardians/{slot}/invite
// creates + links one and emails a set-password link, or sends an existing
// account a consent invite; on a linked slot it re-sends the set-password link).
function useGuardianLinks(apiUrl, getConfig, athleteId) {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    if (!apiUrl || !getConfig || !athleteId) return undefined;
    let alive = true;
    axios.get(`${apiUrl}/admin/athletes/${athleteId}/guardians`, getConfig())
      .then(r => { if (alive) setLinks(r.data); })
      .catch(() => { if (alive) setLinks(null); });
    return () => { alive = false; };
    // getConfig is a fresh closure on every page render; refetching on each
    // one would loop. The token it reads doesn't change while the page is up.
  }, [apiUrl, athleteId]);
  return [links, setLinks];
}

const GUARDIAN_INVITE_RESULT = {
  account_created: e => `Account created. Set-password email sent to ${e}.`,
  invited: e => `${e} already has an account, so they were sent an invite to accept.`,
  setup_resent: e => `Set-password email sent to ${e}.`,
};

function GuardianAccountRow({ slot, link, profileEmail, apiUrl, getConfig, athleteId, onLink }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  if (!link && !profileEmail) return null;

  const send = async () => {
    setBusy(true);
    setNote(null);
    try {
      const { data } = await axios.post(
        `${apiUrl}/admin/athletes/${athleteId}/guardians/${slot}/invite`, {}, getConfig());
      const email = data.link?.email || profileEmail;
      setNote(data.email_sent === false
        ? { error: true, text: `Saved, but the email to ${email} failed to send. Try again.` }
        : { text: (GUARDIAN_INVITE_RESULT[data.status] || (() => 'Done.'))(email) });
      onLink(slot, data.link);
    } catch (err) {
      setNote({ error: true, text: err.response?.data?.detail || 'Could not send. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  let status;
  let label;
  if (link?.state === 'linked') {
    status = <>Account linked: <strong>{link.email}</strong></>;
    label = 'Resend set-password email';
  } else if (link?.state === 'pending') {
    status = <>Invite sent {fmtDate(link.invited_at)} to <strong>{link.email}</strong>, not accepted yet</>;
    label = 'Resend invite';
  } else {
    status = 'No ProZpects account yet';
    label = 'Send guardian invite';
  }
  return (
    <div className="adm-guardian__account" data-testid={`staff_${slot}_account`}>
      <div className="adm-guardian__account-status">{status}</div>
      <button type="button" className="adm-btn adm-btn--sm" onClick={send} disabled={busy}
        data-testid={`staff_${slot}_invite`}>
        {busy ? 'Sending…' : label}
      </button>
      {note && (
        <div className={`adm-guardian__account-note${note.error ? ' adm-guardian__account-note--error' : ''}`}
          role="status">{note.text}</div>
      )}
    </div>
  );
}

export function StaffContactCard({ profile, user, sportSlug, athleteId, apiUrl, getConfig, canManageGuardians }) {
  const safe = profile || {};
  const u = user || {};
  const street = [safe.home_address, safe.address_line_2].filter(Boolean);
  const cityLine = [[safe.city, safe.state].filter(Boolean).join(', '), safe.zip_code].filter(Boolean).join(' ');
  const addressLines = [...street, cityLine].filter(Boolean);
  const phone = safe.cell_phone || u.phone;
  const dob = u.date_of_birth
    ? new Date(u.date_of_birth).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;
  const g1Has = safe.guardian1_name || safe.guardian1_phone || safe.guardian1_email;
  const g2Has = safe.guardian2_name || safe.guardian2_phone || safe.guardian2_email;
  const scList = Array.isArray(safe.sport_coaches) ? safe.sport_coaches : [];
  const sc = scList.find(r => r.sport_slug === sportSlug) || null;
  const hsHas = !!(sc && (sc.hs_coach_name || sc.hs_coach_phone || sc.hs_coach_email));
  const clubHas = !!(sc && (sc.club_coach_name || sc.club_coach_phone || sc.club_coach_email || sc.club_team_name));
  const hasAny = addressLines.length || phone || u.email || dob || g1Has || g2Has || hsHas || clubHas;
  const [links, setLinks] = useGuardianLinks(
    canManageGuardians ? apiUrl : null, getConfig, athleteId);
  const accountRow = (slot, profileEmail) => (links ? (
    <GuardianAccountRow
      slot={slot} link={links[slot]} profileEmail={profileEmail}
      apiUrl={apiUrl} getConfig={getConfig} athleteId={athleteId}
      onLink={(s, l) => setLinks(prev => ({ ...prev, [s]: l }))}
    />
  ) : null);

  return (
    <section className="adm__card adm-rcard">
      <div className="adm-card__head"><h2 className="adm-card__title">Contact</h2></div>
      {!hasAny ? (
        <div className="adm-photos__empty">No contact info on file.</div>
      ) : (
        <>
          <div className="adm-kv">
            {phone && (
              <div className="adm-kv__row">
                <span className="adm-kv__label">Phone</span>
                <span className="adm-kv__value">
                  <a className="adm-plink" href={`tel:${phone}`}>{phone}</a>
                  {safe.cell_phone && u.phone && safe.cell_phone !== u.phone && (
                    <span className="adm-kv__note"> (account: {u.phone})</span>
                  )}
                </span>
              </div>
            )}
            {u.email && (
              <div className="adm-kv__row">
                <span className="adm-kv__label">Email</span>
                <span className="adm-kv__value">{u.email}</span>
              </div>
            )}
            {dob && (
              <div className="adm-kv__row">
                <span className="adm-kv__label">DOB</span>
                <span className="adm-kv__value">{dob}</span>
              </div>
            )}
            {addressLines.length > 0 && (
              <div className="adm-kv__row">
                <span className="adm-kv__label">Address</span>
                <span className="adm-kv__value">{addressLines.map((p, i) => <div key={i}>{p}</div>)}</span>
              </div>
            )}
          </div>
          {g1Has && (
            <StaffContactBlock
              label="Guardian" name={safe.guardian1_name} relation={safe.guardian1_relation}
              phone={safe.guardian1_phone} email={safe.guardian1_email} approved={safe.guardian1_approved}
            >{accountRow('guardian1', safe.guardian1_email)}</StaffContactBlock>
          )}
          {g2Has && (
            <StaffContactBlock
              label="Guardian 2" name={safe.guardian2_name} relation={safe.guardian2_relation}
              phone={safe.guardian2_phone} email={safe.guardian2_email} approved={undefined}
            >{accountRow('guardian2', safe.guardian2_email)}</StaffContactBlock>
          )}
          {hsHas && (
            <StaffContactBlock
              dashed label="High school coach" name={sc.hs_coach_name}
              phone={sc.hs_coach_phone} email={sc.hs_coach_email}
            />
          )}
          {clubHas && (
            <StaffContactBlock
              dashed label="Club coach" name={sc.club_coach_name} relation={sc.club_team_name}
              phone={sc.club_coach_phone} email={sc.club_coach_email}
            />
          )}
        </>
      )}
    </section>
  );
}

const STAFF_ROLE_LABELS = { ...ROLE_LABELS, player_parent: 'Athlete', base_user: 'Athlete' };
const cap = s => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '');

// "Pro · Annual" for an active subscription, its raw status otherwise, "Free"
// with none. Only rendered when the caller passed a membership, which the page
// does only for perms.canSeeFinancial.
function planLabel(membership) {
  const sub = membership.subscription;
  if (!sub) return membership.is_premium_active ? 'Pro' : 'Free';
  const plan = cap(sub.plan_type);
  if (membership.is_premium_active) return plan ? `Pro · ${plan}` : 'Pro';
  return [plan, cap(sub.status)].filter(Boolean).join(' · ') || 'Free';
}

/**
 * Account card. `activity` is the profile payload's staff/partner-only block;
 * when it is absent (any other viewer) the Devices section is not drawn.
 */
export function StaffAccountCard({ profile, user, membership, activity }) {
  const safe = profile || {};
  const u = user || {};
  const sub = membership && membership.subscription;
  const devices = (activity && activity.devices) || [];
  const rows = [
    u.created_at && ['Signed up', fmtDate(u.created_at)],
    ['Role', STAFF_ROLE_LABELS[u.role] || u.role || '—'],
    ['Sport', safe.sport || '—'],
    ['Position', safe.position_primary || '—'],
    ['Grad year', safe.graduation_year || '—'],
    membership && ['Plan', planLabel(membership)],
    sub && sub.current_period_end && [sub.cancel_at_period_end ? 'Ends' : 'Renews', fmtDate(sub.current_period_end)],
  ].filter(Boolean);

  return (
    <section className="adm__card adm-rcard">
      <div className="adm-card__head"><h2 className="adm-card__title">Account</h2></div>
      <div className="adm-kv">
        {rows.map(([k, v]) => (
          <div className="adm-kv__row" key={k}>
            <span className="adm-kv__label">{k}</span>
            <span className="adm-kv__value">{v}</span>
          </div>
        ))}
      </div>
      {activity && (
        <div className="adm-rsection">
          <div className="adm-rsection__label">Devices</div>
          {devices.length === 0 ? (
            <div className="adm-device__none">No app or web activity in the last {activity.window_days || 90} days</div>
          ) : (
            <div className="adm-devices">
              {devices.map((d, i) => (
                <div className="adm-device" key={d.device_id || `web-${i}`}>
                  <span className="adm-device__name">
                    {d.label}
                    {d.update_available && <span className="adm-chip adm-chip--amber">Update available</span>}
                  </span>
                  <span className="adm-device__meta">
                    {[d.app_version && `v${d.app_version}`, fmtLastSeen(d.last_seen_at)].filter(Boolean).join(' · ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function AccountRailCard({ profile, user }) {
  const safe = profile || {};
  const u = user || {};
  const roleLabel = ROLE_LABELS[u.role] || u.role || '—';
  return (
    <div className="ae-card ae-rail-card">
      <div className="ae-rail-card__head"><span className="ae-rail-card__title">Account</span></div>
      <div className="ae-rail-kv">
        <div className="ae-rail-kv__row">
          <span className="ae-rail-kv__label">Role</span>
          <span className="ae-rail-kv__value">{roleLabel}</span>
        </div>
        <div className="ae-rail-kv__row">
          <span className="ae-rail-kv__label">Sport</span>
          <span className="ae-rail-kv__value">{safe.sport || '—'}</span>
        </div>
        <div className="ae-rail-kv__row">
          <span className="ae-rail-kv__label">Position</span>
          <span className="ae-rail-kv__value">{safe.position_primary || '—'}</span>
        </div>
        <div className="ae-rail-kv__row">
          <span className="ae-rail-kv__label">Grad year</span>
          <span className="ae-rail-kv__value">{safe.graduation_year || '—'}</span>
        </div>
      </div>
    </div>
  );
}
